import { z } from 'zod/v4';
import { QuestionnaireRequestAnswerStatisticsSchema } from './questionnaire-request-detail.js';

/**
 * Path parameters of `PATCH /api/questionnaires/:id/requests/:requestId/status`.
 *
 * The legacy route was `PATCH /v1/questionnaires/requests/:id/status` with the
 * questionnaire id in the **body**. It moves into the path here, matching the
 * sibling create, delete, reminder and due-date endpoints — all five now agree
 * on where a request collection lives, and the questionnaire id can no longer
 * disagree with the resource being addressed.
 *
 * `id` is the questionnaire, `requestId` the answer request. Both frontends
 * already hold both ids at every call site, so nothing had to be plumbed
 * through to make this possible.
 */
export const UpdateQuestionnaireRequestStatusParamsSchema = z.object({
  id: z.string().min(1),
  requestId: z.string().min(1),
});

export type UpdateQuestionnaireRequestStatusParams = z.infer<
  typeof UpdateQuestionnaireRequestStatusParamsSchema
>;

/**
 * The five statuses an answer request can be moved to.
 *
 * Ported verbatim from the legacy `REQUEST_STATUSES`, which was
 * `Object.keys(REQUEST_STATUS_ENUM)`. `DELETED` is in the list because legacy
 * accepted it, not because anything sends it — no button in either frontend
 * emits it, and the bulk-delete endpoint soft-deletes via `is_removed` rather
 * than through this status. It stays so an API-key client that has been setting
 * it keeps working; dropping it would be a silent 400 on a call that succeeds
 * today.
 *
 * Note this is a list of *targets*, not a state machine: legacy validated the
 * value and the caller's relationship to the questionnaire, never the transition
 * itself. `COMPLETED` → `OPEN` is as accepted as `OPEN` → `IN_REVIEW`.
 */
export const QUESTIONNAIRE_REQUEST_STATUSES = [
  'OPEN',
  'IN_REVIEW',
  'COMPLETED',
  'REQUEST_CHANGES',
  'DELETED',
] as const;

export type QuestionnaireRequestStatus =
  (typeof QUESTIONNAIRE_REQUEST_STATUSES)[number];

/**
 * Body of `PATCH /api/questionnaires/:id/requests/:requestId/status`.
 *
 * Only `status` remains — the legacy body's `questionnaireId` is now the `:id`
 * path param. The legacy JSON Schema declared no `additionalProperties: false`,
 * so unknown keys were accepted and ignored; zod's default object strips them,
 * which is the same observable behaviour.
 */
export const UpdateQuestionnaireRequestStatusBodySchema = z.object({
  status: z.enum(QUESTIONNAIRE_REQUEST_STATUSES),
});

export type UpdateQuestionnaireRequestStatusBody = z.infer<
  typeof UpdateQuestionnaireRequestStatusBodySchema
>;

/**
 * The answer request as this endpoint returns it — the one route in the migrated
 * questionnaire family that answers with a body rather than a 204.
 *
 * It is kept because the endpoint is reachable by API key, not only by the UI:
 * both frontends discard the payload today, but dropping it would be a breaking
 * change for any integration reading the updated row back. What changed is the
 * envelope — `reply.apiSuccess` wraps it in `{ data }`, the same move the
 * already-migrated `GET /api/questionnaires/requests/:id` made.
 *
 * The shape is the legacy `requestDB.getById` projection run through
 * `buildRequest`, which for this query means exactly three things: `statistics`
 * parsed from its JSON text, and `supplierConnected` / `answeredByConnected`
 * coerced with `Boolean(undefined)`. Neither connection flag is selected by that
 * query, so both are **always `false`** here — unlike on the detail read, which
 * computes them properly. The legacy integration test asserted only
 * `typeof === 'boolean'`, and the frontends read connectivity off the detail
 * endpoint, so the constant is preserved rather than "fixed" into two extra
 * profile lookups nobody consumes.
 *
 * `looseObject` mirrors the legacy response schema's `additionalProperties: true`.
 * Date columns are left undeclared so they pass through as `Date` and serialise
 * to ISO strings — the convention the rest of this module's schemas set.
 */
export const UpdateQuestionnaireRequestStatusResponseSchema = z.looseObject({
  id: z.string(),
  questionnaireId: z.string(),
  forCompanyId: z.string().nullable().optional(),
  onBehalfOfCompanyId: z.string().nullable().optional(),
  createdByCompanyId: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  statistics: QuestionnaireRequestAnswerStatisticsSchema.nullable().optional(),
  /** `0`/`1` — the request was soft-deleted. Selected but never filtered on. */
  isRemoved: z.number().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByUserId: z.string().nullable().optional(),
  questionnaireName: z.string().nullable().optional(),
  /** Always `false` on this endpoint — see the schema doc above. */
  supplierConnected: z.boolean(),
  /** Always `false` on this endpoint — see the schema doc above. */
  answeredByConnected: z.boolean(),
});

export type UpdateQuestionnaireRequestStatusResponse = z.infer<
  typeof UpdateQuestionnaireRequestStatusResponseSchema
>;
